import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TEST_LOGIN_ENABLED } from '../api/client';

/**
 * Landing page with Google login button.
 */
function Landing() {
  const { isAuthenticated, isLoading, login, testLogin, error } = useAuth();
  const navigate = useNavigate();
  const [testUsername, setTestUsername] = useState('');
  const [testPassword, setTestPassword] = useState('');

  // Redirect to the protected workspace if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const handleTestLogin = async (event: FormEvent) => {
    event.preventDefault();
    await testLogin(testUsername, testPassword);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex flex-col">
      {/* Header */}
      <header className="p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-white">Dada Agent</h1>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          {/* Hero card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Icon */}
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Welcome to Dada Agent
            </h2>
            <p className="text-gray-600 mb-8">
              Sign in with your Google account to start chatting with our AI assistant.
            </p>

            {/* Error message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
                {error === 'network' ? (
                  <>
                    <p className="text-red-700 text-sm font-medium">Network connection lost</p>
                    <p className="text-red-600 text-xs mt-1">
                      Check your internet connection and click the button below to try again.
                    </p>
                  </>
                ) : (
                  <p className="text-red-700 text-sm">{error}</p>
                )}
              </div>
            )}

            {/* Google Sign-In Button */}
            <button
              onClick={login}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 rounded-lg px-6 py-3 text-gray-700 font-medium hover:bg-gray-50 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
              ) : (
                <>
                  {/* Google Icon */}
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>

            {TEST_LOGIN_ENABLED && (
              <form onSubmit={handleTestLogin} data-testid="test-login-form" className="mt-6 space-y-3 text-left">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Testing login</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Username</span>
                  <input
                    type="text"
                    data-testid="test-login-username"
                    autoComplete="username"
                    value={testUsername}
                    onChange={(event) => setTestUsername(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    disabled={isLoading}
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 mb-1">Password</span>
                  <input
                    type="password"
                    data-testid="test-login-password"
                    autoComplete="current-password"
                    value={testPassword}
                    onChange={(event) => setTestPassword(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    disabled={isLoading}
                  />
                </label>
                <button
                  type="submit"
                  data-testid="test-login-submit"
                  disabled={isLoading || !testUsername || !testPassword}
                  className="w-full rounded-lg bg-primary-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Sign in for testing
                </button>
              </form>
            )}

            {/* Footer text */}
            <p className="mt-6 text-xs text-gray-500">
              By signing in, you agree to our Terms of Service and Privacy Policy.
            </p>
          </div>

          {/* Features */}
          <div className="mt-8 grid grid-cols-3 gap-4 text-center text-white">
            <div>
              <div className="text-2xl mb-1">🔒</div>
              <div className="text-sm opacity-90">Secure</div>
            </div>
            <div>
              <div className="text-2xl mb-1">⚡</div>
              <div className="text-sm opacity-90">Fast</div>
            </div>
            <div>
              <div className="text-2xl mb-1">🤖</div>
              <div className="text-sm opacity-90">AI-Powered</div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-white/70 text-sm">
        <p>Dada Agent • Built with React, FastAPI, and AWS</p>
      </footer>
    </div>
  );
}

export default Landing;
